<script>
import axios from 'axios'
import Incidents from '../incidents/incidents'
import { formatCount, formatDate, windowLabel } from './formatters'

const SEARCH_DEBOUNCE_MS = 400
const WINDOW_HOURS = [24, 72, 168, 720, 2160, 8760, 0]
const DEFAULT_HOURS = 168
const LIMIT_OPTIONS = [10, 25, 50, 100]
const DEFAULT_LIMIT = 25
const CHART_ROWS = 15
const SEVERITIES = ['critical', 'high', 'medium', 'low']
const SEVERITY_VARIANT = { critical: 'dark', high: 'danger', medium: 'warning', low: 'success' }
const SEVERITY_COLOR = { critical: '#313a46', high: '#fa5c7c', medium: '#ffbc00', low: '#0acf97' }

const RANKINGS = [
  {
    rank: 'cases',
    title: 'Most cases',
    help: 'Endpoints involved in the most cases in the window. One case counts once per endpoint it involved.',
    metric: 'cases',
    metricLabel: 'Cases'
  },
  {
    rank: 'severity',
    title: 'Most critical',
    help: 'Endpoints with the highest severity score: critical cases weigh 10, high 5, medium 2 and low 1. A manual severity set by an analyst wins over the one Cortex assigned.',
    metric: 'score',
    metricLabel: 'Score'
  }
]

export default {
  components: { Incidents },
  data () {
    return {
      // eslint-disable-next-line
      appConfig: process.env.NODE_ENV === 'production' ? require('@src/app.config') : require('@src/app.config.dev'),
      rankings: RANKINGS,
      // Filters, mirrored in the query string so a view can be shared.
      hours: DEFAULT_HOURS,
      limit: DEFAULT_LIMIT,
      resolved: false,
      agentsOnly: false,
      q: '',
      host: '',
      searchText: '',
      searchTimer: null,
      ready: false,
      results: {},
      busy: {},
      errors: {},
      search: null,
      searchBusy: false,
      searchError: '',
      summary: null,
      summaryBusy: false,
      summaryError: '',
      exporting: '',
      lastUpdated: null,
      limitOptions: LIMIT_OPTIONS,
      severities: SEVERITIES
    }
  },
  computed: {
    baseURL () {
      return this.appConfig.xdrAPI.baseURL || 'http://localhost:5000'
    },
    windowOptions () {
      return WINDOW_HOURS.map((value) => ({ value, text: value ? windowLabel(value) : 'All time (slow)' }))
    },
    windowText () {
      return this.hours ? windowLabel(this.hours).toLowerCase() : 'all time'
    },
    scopeText () {
      const parts = [this.windowText]
      if (this.agentsOnly) parts.push('agents only')
      parts.push(this.resolved ? 'all statuses' : 'without false positives and duplicates')
      return parts.join(' · ')
    },
    isBusy () {
      return Object.keys(this.busy).some((key) => this.busy[key])
    },
    searchItems () {
      return (this.search && this.search.items) || []
    },
    // The incidents table reuses the generic incidents endpoint; the url must
    // already carry "?" because the component appends "&page=..." to it.
    incidentsUrl () {
      const params = ['include=incident.incident,assets,netinfo', `host=${encodeURIComponent(this.host)}`]
      if (this.hours) params.push(`created_after_minutes=${this.hours * 60}`)
      if (!this.resolved) params.push('nostatus=resolved_false_positive,resolved_duplicate')
      return `/pan/xdr/incidents?${params.join('&')}`
    },
    tableFields () {
      return [
        { key: 'position', label: '#', thStyle: { width: '40px' } },
        { key: 'key', label: 'Endpoint' },
        { key: 'cases', label: 'Cases', class: 'text-right' },
        { key: 'severity', label: 'C / H / M / L', class: 'text-center text-nowrap' },
        { key: 'score', label: 'Score', class: 'text-right' },
        { key: 'open', label: 'Open', class: 'text-right' },
        { key: 'last_case', label: 'Last case', class: 'text-nowrap', formatter: (value) => formatDate(value) }
      ]
    }
  },
  watch: {
    hours () { this.onFilterChange(true) },
    limit () { this.onFilterChange(false) },
    resolved () { this.onFilterChange(true) },
    agentsOnly () { this.onFilterChange(false) },
    searchText (value) {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => { this.q = value.trim() }, SEARCH_DEBOUNCE_MS)
    },
    q () {
      if (!this.ready) return
      this.syncQuery()
      this.loadSearch()
    },
    host () {
      if (!this.ready) return
      this.syncQuery()
      this.loadSummary()
    }
  },
  mounted () {
    this.readQuery()
    this.$nextTick(() => {
      this.ready = true
      this.loadAll()
    })
  },
  beforeDestroy () {
    clearTimeout(this.searchTimer)
  },
  methods: {
    formatCount,
    formatDate,
    parse (data) {
      return typeof data === 'string' ? JSON.parse(data) : data
    },
    // --- query string -----------------------------------------------------
    readQuery () {
      const query = this.$route.query || {}
      const hours = parseInt(query.hours, 10)
      this.hours = WINDOW_HOURS.includes(hours) ? hours : DEFAULT_HOURS
      const limit = parseInt(query.limit, 10)
      this.limit = LIMIT_OPTIONS.includes(limit) ? limit : DEFAULT_LIMIT
      this.resolved = query.resolved === 'yes'
      this.agentsOnly = query.agents === 'yes'
      this.q = query.q || ''
      this.searchText = this.q
      this.host = query.host || ''
    },
    syncQuery () {
      const query = {}
      if (this.host) query.host = this.host
      if (this.hours !== DEFAULT_HOURS) query.hours = String(this.hours)
      if (this.limit !== DEFAULT_LIMIT) query.limit = String(this.limit)
      if (this.resolved) query.resolved = 'yes'
      if (this.agentsOnly) query.agents = 'yes'
      if (this.q) query.q = this.q
      if (JSON.stringify(this.$route.query || {}) === JSON.stringify(query)) return
      // Replace rather than push: a filter tweak is not a history entry.
      this.$router.replace({ query }, () => {}, () => {})
    },
    toQuery (params) {
      return Object.keys(params)
        .filter((key) => params[key] !== '' && params[key] !== null && params[key] !== undefined && params[key] !== false)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key] === true ? 'yes' : params[key])}`)
        .join('&')
    },
    filterParams (extra) {
      return Object.assign({ hours: this.hours, resolved: this.resolved, agents: this.agentsOnly }, extra || {})
    },
    onFilterChange (affectsEndpoint) {
      if (!this.ready) return
      this.syncQuery()
      this.loadRankings()
      if (this.q) this.loadSearch()
      if (affectsEndpoint && this.host) this.loadSummary()
    },
    // --- loading ----------------------------------------------------------
    loadAll () {
      this.loadRankings()
      if (this.q) this.loadSearch()
      if (this.host) this.loadSummary()
    },
    loadRankings () {
      const requests = this.rankings.map((entry) => this.loadRanking(entry.rank))
      Promise.all(requests).finally(() => { this.lastUpdated = new Date() })
    },
    loadRanking (rank) {
      const params = this.filterParams({ rank, limit: this.limit })
      this.$set(this.busy, rank, true)
      return axios.get(`${this.baseURL}/pan/xdr/endpoints/top?${this.toQuery(params)}`)
        .then((response) => {
          const data = this.parse(response.data)
          this.$set(this.results, rank, data)
          this.$set(this.errors, rank, data.error || '')
        })
        .catch((error) => {
          this.$set(this.errors, rank, `Could not load this ranking: ${error.message}`)
        })
        .finally(() => {
          this.$set(this.busy, rank, false)
        })
    },
    loadSearch () {
      const q = this.q
      if (!q) {
        this.search = null
        this.searchError = ''
        return Promise.resolve()
      }
      this.searchBusy = true
      const params = this.filterParams({ q })
      return axios.get(`${this.baseURL}/pan/xdr/endpoints/search?${this.toQuery(params)}`)
        .then((response) => {
          if (q !== this.q) return
          const data = this.parse(response.data)
          this.search = data
          this.searchError = data.error || ''
        })
        .catch((error) => {
          if (q === this.q) this.searchError = `Could not search: ${error.message}`
        })
        .finally(() => { this.searchBusy = false })
    },
    loadSummary () {
      const host = this.host
      if (!host) {
        this.summary = null
        this.summaryError = ''
        return Promise.resolve()
      }
      this.summaryBusy = true
      const params = this.filterParams({ host })
      return axios.get(`${this.baseURL}/pan/xdr/endpoints/summary?${this.toQuery(params)}`)
        .then((response) => {
          if (host !== this.host) return
          const data = this.parse(response.data)
          this.summary = data
          this.summaryError = data.error || ''
        })
        .catch((error) => {
          if (host === this.host) this.summaryError = `Could not load this endpoint: ${error.message}`
        })
        .finally(() => { this.summaryBusy = false })
    },
    downloadCsv (rank) {
      const params = this.filterParams({ rank })
      const filename = `endpoints_by_${rank}.csv`
      this.exporting = rank
      axios
        .get(`${this.baseURL}/pan/xdr/endpoints/top/csv?${this.toQuery(params)}`, { responseType: 'blob' })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          link.remove()
          window.URL.revokeObjectURL(url)
        })
        .catch((error) => {
          this.$set(this.errors, rank, `Could not export ${filename}: ${error.message}`)
        })
        .finally(() => { this.exporting = '' })
    },
    // --- navigation -------------------------------------------------------
    openEndpoint (item) {
      this.host = item.key
      this.$nextTick(() => {
        const section = this.$refs.endpoint
        if (section && section.scrollIntoView) section.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    closeEndpoint () {
      this.host = ''
    },
    // --- presentation -----------------------------------------------------
    items (rank) {
      return (this.results[rank] && this.results[rank].items) || []
    },
    chartItems (rank) {
      return this.items(rank).slice(0, CHART_ROWS)
    },
    chartSeries (entry) {
      return [{ name: entry.metricLabel, data: this.chartItems(entry.rank).map((item) => item[entry.metric] || 0) }]
    },
    chartOptions (entry) {
      const items = this.chartItems(entry.rank)
      return {
        chart: { type: 'bar', toolbar: { show: false }, animations: { enabled: false }, events: { dataPointSelection: (event, chart, config) => { const item = items[config.dataPointIndex]; if (item) this.openEndpoint(item) } } },
        plotOptions: { bar: { horizontal: true, barHeight: '60%', borderRadius: 3, dataLabels: { position: 'top', hideOverflowingLabels: false } } },
        dataLabels: { enabled: true, formatter: (value) => formatCount(value), offsetX: 24, style: { fontSize: '11px' } },
        colors: [entry.rank === 'severity' ? SEVERITY_COLOR.high : '#727cf5'],
        xaxis: { categories: items.map((item) => item.key), labels: { formatter: (value) => formatCount(Number(value)) } },
        yaxis: { labels: { maxWidth: 180 } },
        tooltip: { y: { formatter: (value) => formatCount(value) } },
        grid: { padding: { right: 32 } }
      }
    },
    chartHeight (count) {
      return Math.max(160, count * 26)
    },
    severityVariant (level) {
      return SEVERITY_VARIANT[level] || 'light'
    },
    osLabel (type) {
      if (!type) return ''
      if (type === 'NO_HOST') return 'No agent'
      return type.replace(/^AGENT_OS_/, '').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
    },
    osList (item) {
      return (item.os_types || []).map(this.osLabel).join(', ')
    },
    cortexUrl (incidentId) {
      return incidentId ? `https://corona.xdr.us.paloaltonetworks.com/incident-view?caseId=${incidentId}` : ''
    }
  }
}
</script>

<template>
  <div class="endpoint-cases">
    <!-- Toolbar -->
    <div class="d-flex align-items-center mb-2">
      <span
        v-if="lastUpdated"
        class="text-muted font-size-12"
      >
        Updated {{ formatDate(lastUpdated) }} (America/Bogota)
      </span>
      <b-button
        size="sm"
        variant="outline-primary"
        class="ml-auto"
        :disabled="isBusy"
        @click="loadAll"
      >
        <feather
          type="refresh-cw"
          class="icon-xs mr-1"
        />
        Refresh
      </b-button>
    </div>

    <!-- Filters -->
    <div class="card mb-3">
      <div class="card-body py-2">
        <div class="row">
          <div class="col-lg-2 col-md-4 col-6 mb-2">
            <label class="font-size-12 text-muted mb-0">Window</label>
            <b-form-select
              v-model="hours"
              size="sm"
              :options="windowOptions"
            />
          </div>
          <div class="col-lg-1 col-md-2 col-6 mb-2">
            <label class="font-size-12 text-muted mb-0">Top</label>
            <b-form-select
              v-model="limit"
              size="sm"
              :options="limitOptions"
            />
          </div>
          <div class="col-lg-3 col-md-6 col-12 mb-2">
            <label class="font-size-12 text-muted mb-0">
              Find an endpoint
              <feather
                v-b-tooltip.hover
                type="help-circle"
                class="help-tip"
                title="Hostname, IP address or Cortex agent id, matched anywhere in the name. Searches the cases in the window; pick All time for older ones. An exact IP also finds the agent hosts that had it."
              />
            </label>
            <b-form-input
              v-model="searchText"
              size="sm"
              type="search"
              placeholder="Hostname, IP or Cortex agent id..."
            />
          </div>
          <div class="col-lg col-md-6 col-12 mb-2 d-flex align-items-end flex-wrap">
            <b-form-checkbox
              v-model="agentsOnly"
              switch
              class="font-size-13 mr-3 mb-1 text-nowrap"
            >
              Agents only
              <feather
                v-b-tooltip.hover
                type="help-circle"
                class="help-tip"
                title="Hide hosts without a Cortex agent (seen only by a firewall or another sensor and identified by IP). Gateways and servers in that group often collect many firewall cases."
              />
            </b-form-checkbox>
            <b-form-checkbox
              v-model="resolved"
              switch
              class="font-size-13 mb-1 text-nowrap"
            >
              Include false positives
              <feather
                v-b-tooltip.hover
                type="help-circle"
                class="help-tip"
                title="Count cases closed as false positive or duplicate too. They are left out by default because they say nothing about the endpoint."
              />
            </b-form-checkbox>
          </div>
        </div>
      </div>
    </div>

    <!-- Search results -->
    <div
      v-if="q"
      class="card mb-3"
    >
      <div class="card-body py-2">
        <h5 class="font-size-14 mb-1">
          <feather
            type="search"
            class="icon-xs mr-1 text-muted"
          />
          Endpoints matching "{{ q }}"
          <small
            v-if="search && !searchBusy"
            class="text-muted"
          >· {{ searchItems.length }} of {{ search.endpoints_total || 0 }} · {{ windowText }}</small>
        </h5>
        <div
          v-if="searchError"
          class="text-danger py-2 font-size-13"
        >
          {{ searchError }}
        </div>
        <div
          v-else-if="searchBusy"
          class="text-muted py-2"
        >
          <b-spinner small />&nbsp;&nbsp;Searching...
        </div>
        <div
          v-else-if="!searchItems.length"
          class="text-muted py-2 font-size-13"
        >
          No endpoint matches in {{ windowText }}.
        </div>
        <div
          v-else
          class="d-flex flex-wrap"
        >
          <a
            v-for="item in searchItems"
            :key="item.key"
            href="javascript:void(0)"
            class="search-hit mr-2 mb-2"
            :class="{ 'search-hit--active': item.key === host }"
            @click="openEndpoint(item)"
          >
            <b>{{ item.key }}</b>
            <span class="text-muted font-size-12"> · {{ osList(item) }} · {{ formatCount(item.cases) }} case{{ item.cases === 1 ? '' : 's' }}</span>
          </a>
        </div>
      </div>
    </div>

    <!-- Endpoint view -->
    <div
      v-if="host"
      ref="endpoint"
      class="card mb-3 endpoint-view"
    >
      <div class="card-body py-2">
        <div class="d-flex align-items-center flex-wrap mb-1 endpoint-header">
          <h4 class="my-0 mr-2 d-flex align-items-center">
            <feather
              type="monitor"
              class="icon-sm mr-1 text-muted"
            />
            {{ host }}
          </h4>
          <template v-if="summary && summary.found">
            <b-badge
              v-for="type in summary.os_types"
              :key="type"
              :variant="type === 'NO_HOST' ? 'secondary' : 'light'"
              class="mr-1 align-self-center"
            >
              {{ osLabel(type) }}
            </b-badge>
          </template>
          <b-button
            size="sm"
            variant="link"
            class="ml-auto py-0 d-flex align-items-center"
            @click="closeEndpoint"
          >
            <feather
              type="x"
              class="icon-xs mr-1"
            />
            Back to rankings
          </b-button>
        </div>
        <div
          v-if="summaryError"
          class="text-danger py-2 font-size-13"
        >
          {{ summaryError }}
        </div>
        <div
          v-else-if="summaryBusy && !summary"
          class="text-muted py-2"
        >
          <b-spinner small />&nbsp;&nbsp;Loading endpoint...
        </div>
        <div
          v-else-if="summary && !summary.found"
          class="text-muted py-2 font-size-13"
        >
          No case involved this endpoint in {{ windowText }}{{ resolved ? '' : ' (false positives and duplicates excluded)' }}.
        </div>
        <template v-else-if="summary">
          <div class="row">
            <div class="col-xl-2 col-md-4 col-6 mb-2">
              <div class="text-muted font-size-12">
                Cases · {{ windowText }}
              </div>
              <h3 class="mb-0">
                {{ formatCount(summary.cases) }}
                <small class="text-muted font-size-13">{{ formatCount(summary.open) }} open</small>
              </h3>
            </div>
            <div class="col-xl-3 col-md-4 col-6 mb-2">
              <div class="text-muted font-size-12">
                By severity · score {{ formatCount(summary.score) }}
              </div>
              <h3 class="mb-0">
                <b-badge
                  v-for="level in severities"
                  :key="level"
                  :variant="severityVariant(level)"
                  class="mr-1 font-size-13 align-middle"
                  :title="level"
                >
                  {{ summary[level] }}
                </b-badge>
              </h3>
            </div>
            <div class="col-xl-3 col-md-4 col-12 mb-2">
              <div class="text-muted font-size-12">
                First and last case
              </div>
              <div class="font-size-13">
                {{ formatDate(summary.first_case) }}<br>
                {{ formatDate(summary.last_case) }}
                <a
                  v-if="summary.last_incident_id"
                  :href="cortexUrl(summary.last_incident_id)"
                  target="_blank"
                  rel="noopener"
                  class="ml-1"
                  title="Open the latest case in Cortex"
                >#{{ summary.last_incident_id }}</a>
              </div>
            </div>
            <div class="col-xl-4 col-md-12 col-12 mb-2">
              <div class="text-muted font-size-12">
                Seen as
              </div>
              <div class="font-size-13">
                <template v-if="summary.names.length">
                  <b>Names:</b> {{ summary.names.join(', ') }}<br>
                </template>
                <template v-if="summary.ips.length">
                  <b>IPs:</b> {{ summary.ips.join(', ') }}<br>
                </template>
                <template v-if="summary.endpoint_ids.length">
                  <b>Cortex agent id:</b> <span class="text-break">{{ summary.endpoint_ids.join(', ') }}</span>
                </template>
                <span
                  v-if="!summary.names.length && !summary.ips.length && !summary.endpoint_ids.length"
                  class="text-muted"
                >No alert names it as a host{{ summary.peers && summary.peers.length ? '; it appears as the other side of a connection' : '' }}.</span>
              </div>
            </div>
          </div>
          <div
            v-if="summary.peers && summary.peers.length"
            class="mb-2"
          >
            <div class="text-muted font-size-12">
              Related IPs
              <feather
                v-b-tooltip.hover
                type="help-circle"
                class="help-tip"
                title="The other side of the connections in this endpoint's alerts: who it talked to, or who reached it. The number is how many alerts carried that IP. Click one to open it."
              />
              <span v-if="summary.peers_total > summary.peers.length">· {{ summary.peers.length }} of {{ summary.peers_total }}</span>
            </div>
            <div class="font-size-13">
              <a
                v-for="peer in summary.peers"
                :key="peer.ip"
                href="javascript:void(0)"
                class="peer-ip mr-1 mb-1"
                :title="`${peer.count} alert${peer.count === 1 ? '' : 's'} with ${peer.ip}`"
                @click="openEndpoint({ key: peer.ip })"
              >{{ peer.ip }} <span class="text-muted">{{ peer.count }}</span></a>
            </div>
          </div>
          <div class="text-muted font-size-12 mb-2">
            Cases that involved this endpoint, newest first. Open a case for its alerts, assets and indicators.
          </div>
          <Incidents
            :key="incidentsUrl"
            :url="incidentsUrl"
          />
        </template>
      </div>
    </div>

    <!-- Rankings -->
    <div class="row">
      <div
        v-for="entry in rankings"
        :key="entry.rank"
        class="col-xl-6 col-12 mb-3"
      >
        <div class="card h-100 mb-0">
          <div class="card-body py-2">
            <div class="d-flex align-items-center mb-1 flex-wrap">
              <h5 class="font-size-14 mb-0">
                <feather
                  :type="entry.rank === 'severity' ? 'alert-triangle' : 'bar-chart-2'"
                  class="icon-xs mr-1 text-muted"
                />
                {{ entry.title }}
                <feather
                  v-b-tooltip.hover
                  type="help-circle"
                  class="help-tip"
                  :title="entry.help"
                />
                <small
                  v-if="results[entry.rank] && !busy[entry.rank]"
                  class="text-muted"
                >
                  · {{ items(entry.rank).length }} of {{ formatCount(results[entry.rank].endpoints_total || 0) }} endpoints
                </small>
              </h5>
              <b-button
                size="sm"
                variant="outline-secondary"
                class="ml-auto"
                title="Export this ranking (up to 1000 rows) as CSV"
                :disabled="exporting.length > 0 || !items(entry.rank).length"
                @click="downloadCsv(entry.rank)"
              >
                <feather
                  type="download"
                  class="icon-xs mr-1"
                />
                CSV
              </b-button>
            </div>
            <div class="text-muted font-size-12 mb-1">
              Top {{ limit }} by {{ entry.metricLabel.toLowerCase() }} · {{ scopeText }}
            </div>

            <div
              v-if="errors[entry.rank]"
              class="text-danger py-4 text-center font-size-13"
            >
              {{ errors[entry.rank] }}
            </div>
            <div
              v-else-if="busy[entry.rank] && !items(entry.rank).length"
              class="text-muted py-4 text-center"
            >
              <b-spinner small />&nbsp;&nbsp;Loading ranking...
            </div>
            <div
              v-else-if="!items(entry.rank).length"
              class="text-muted py-4 text-center"
            >
              No case involved an endpoint in {{ windowText }}.
            </div>
            <template v-else>
              <apexchart
                type="bar"
                :height="chartHeight(chartItems(entry.rank).length)"
                :series="chartSeries(entry)"
                :options="chartOptions(entry)"
              />
              <div class="table-responsive mb-0">
                <b-table
                  :items="items(entry.rank)"
                  :fields="tableFields"
                  small
                  hover
                  class="ranking-table font-size-13"
                  @row-clicked="openEndpoint"
                >
                  <template v-slot:cell(key)="data">
                    <a
                      href="javascript:void(0)"
                      class="text-break"
                      :class="{ 'font-weight-bold': data.item.key === host }"
                      @click.stop="openEndpoint(data.item)"
                    >{{ data.item.key }}</a>
                    <div class="text-muted font-size-11">
                      {{ osList(data.item) }}
                    </div>
                  </template>
                  <template v-slot:cell(cases)="data">
                    {{ formatCount(data.item.cases) }}
                  </template>
                  <template v-slot:cell(severity)="data">
                    <b-badge
                      v-for="level in severities"
                      :key="level"
                      :variant="data.item[level] ? severityVariant(level) : 'light'"
                      class="mr-1"
                      :title="`${data.item[level]} ${level}`"
                    >
                      {{ data.item[level] }}
                    </b-badge>
                  </template>
                  <template v-slot:cell(score)="data">
                    {{ formatCount(data.item.score) }}
                  </template>
                  <template v-slot:cell(open)="data">
                    <span :class="data.item.open ? '' : 'text-muted'">{{ formatCount(data.item.open) }}</span>
                  </template>
                </b-table>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Help icons: quiet and centred on the text they explain. The feather
   component's root element carries only its own scope attribute, so a plain
   scoped rule never reaches it; hence the deep selectors, and the explicit
   size because the theme colours and sizes every feather icon. */
.endpoint-cases >>> .help-tip {
  cursor: help;
  vertical-align: middle;
  margin-top: -2px;
  color: #ced4da;
}
.endpoint-cases >>> .help-tip svg {
  width: 13px;
  height: 13px;
  vertical-align: top;
}
.endpoint-cases >>> .help-tip:hover {
  color: #98a6ad;
}
.ranking-table >>> td {
  vertical-align: middle;
}
.ranking-table >>> tr {
  cursor: pointer;
}
.search-hit {
  display: inline-block;
  padding: 2px 8px;
  border: 1px solid #e3eaef;
  border-radius: 4px;
  color: inherit;
}
.search-hit:hover,
.search-hit--active {
  border-color: #727cf5;
  text-decoration: none;
}
.endpoint-view {
  border-left: 3px solid #727cf5;
}
.peer-ip {
  display: inline-block;
  padding: 0 6px;
  border: 1px solid #e3eaef;
  border-radius: 4px;
  color: inherit;
}
.peer-ip:hover {
  border-color: #727cf5;
  text-decoration: none;
}
/* Feather icons are inline-block boxes taller than the text beside them;
   in a flex row they centre only once the svg itself stops sitting on the
   baseline. */
.endpoint-header >>> .feather,
.endpoint-header >>> .feather svg {
  display: block;
}
/* The theme paints every ApexCharts data label white, which suits labels
   inside a bar. Here they sit past the end of the bar, on the card. */
.endpoint-cases >>> .apexcharts-datalabel {
  fill: #6c757d !important;
}
</style>
